import React, { useState, useEffect } from 'react';
import P, { InferProps } from 'prop-types';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { Option, OptionsGroup, SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import { ListSubheader } from '@mui/material';

const styles: SxStyle = {
  formControl: {
    minWidth: 214,
  },
  header: (theme) => ({
    fontSize: '16px',
    color: theme.palette.menu.main,
    '&::first-letter': {
      textTransform: 'capitalize',
    },
  }),
};

function MultipleSelectField({
  field,
  setChange,
  setValidation,
  isReadOnly,
  fullWidth = false,
  checkboxes = false,
  hideValidationError,
}: InferProps<typeof MultipleSelectField.propTypes>) {
  const { strings } = useStringsContext();
  const [value, setValue] = useState<(string | number | boolean)[]>(field.value || []);
  const [open, setOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDirty, setIsdirty] = useState(false);

  useEffect(() => {
    setValue(field.value);
  }, [field]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e: SelectChangeEvent<(string | number | boolean)[]>) => {
    const value = e.target.value;
    if (Array.isArray(value)) {
      setValue(value);

      if (setChange) {
        setChange(field.key, value, field.validation, field.isTeamDataValue);
      }

      setIsdirty(true);
    }
  };

  useEffect(() => {
    const validate = async (newValue: (string | number | boolean)[]) => {
      const yupSchema = field.validation;
      const parsedValue: (string | number | boolean)[] = newValue;

      if (!yupSchema || !setValidation) {
        return;
      }

      try {
        await yupSchema.validate(newValue);
        setErrorMessage('');
      } catch (error: any) {
        if (isDirty || !hideValidationError) {
          setErrorMessage(error.message);
        }
      } finally {
        if (setValidation) {
          setValidation(field.key, parsedValue, field.validation);
        }
      }
    };

    validate(value);
  }, [hideValidationError, field, isDirty, setValidation, value]);

  const renderValue = (value: (string | number | boolean)[]) => {
    return value
      .map((val) => {
        if (field.options && field.optionsText) {
          const optionsIndex = field.options.indexOf(val);
          if (optionsIndex > -1 && optionsIndex < field.optionsText.length) {
            return field.optionsText[optionsIndex];
          }
        }
        if (field.groupedOptions) {
          const optionsGroup = field.groupedOptions.find((group) =>
            group.options.find((option) => option === val)
          )!;
          if (optionsGroup?.optionsText?.length) {
            const optionsIndex = optionsGroup?.options.indexOf(val);
            if (optionsIndex > -1 && optionsIndex < optionsGroup.optionsText.length) {
              return optionsGroup.optionsText[optionsIndex];
            }
          }
        }
        return val;
      })
      .join(', ');
  };

  const renderOption = (
    option: Option,
    optionsText: string | undefined | null,
    isDisabled: boolean | undefined
  ) => {
    const text = optionsText || option;
    return (
      <MenuItem key={`${field.id}-${option}`} value={option as string} disabled={isDisabled}>
        {checkboxes && <Checkbox checked={value.indexOf(option) > -1} />}
        {text === '' ? <em>{strings.messages.none}</em> : (text as string)}
      </MenuItem>
    );
  };

  const renderGroup = (optionsGroup: OptionsGroup) => {
    const options = optionsGroup.options?.map((option, index) =>
      renderOption(option, optionsGroup.optionsText?.[index], false)
    );
    return [
      <ListSubheader sx={styles.header} color="primary" key={optionsGroup.name}>
        {optionsGroup.name}
      </ListSubheader>,
      options,
    ];
  };

  return (
    <>
      <FormControl variant="outlined" sx={styles.formControl} fullWidth={fullWidth || false}>
        <InputLabel id={`${field.id}-label`} htmlFor={field.id}>
          {field.title}
        </InputLabel>
        <Select
          disabled={field.key === 'Not available' || !!isReadOnly}
          labelId={`${field.id}-label`}
          id={field.id}
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          value={value}
          onChange={handleChange}
          displayEmpty
          renderValue={renderValue}
          color="primary"
          label={field.title}
          multiple
        >
          {field.groupedOptions
            ? field.groupedOptions.map((group) => renderGroup(group))
            : field.options?.map((option: Option, index) =>
                renderOption(
                  option,
                  field.optionsText?.[index],
                  field.optionsDisabled?.includes(option as string)
                )
              )}
        </Select>
        <FormHelperText error>{errorMessage}</FormHelperText>
      </FormControl>
    </>
  );
}

MultipleSelectField.propTypes = {
  setChange: P.func,
  setValidation: P.func,
  isReadOnly: P.bool,
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any,
    key: P.string,
    groupedOptions: P.arrayOf(
      P.shape({
        name: P.string.isRequired,
        options: P.arrayOf(P.oneOfType([P.string, P.number, P.bool]).isRequired).isRequired,
        optionsText: P.arrayOf(P.string.isRequired),
      }).isRequired
    ),
    options: P.arrayOf(P.oneOfType([P.string, P.number, P.bool]).isRequired),
    optionsText: P.arrayOf(P.string.isRequired),
    optionsDisabled: P.arrayOf(P.string),
    value: P.arrayOf(P.oneOfType([P.string, P.number, P.bool]).isRequired).isRequired,
    isTeamDataValue: P.bool,
  }).isRequired,
  fullWidth: P.bool,
  checkboxes: P.bool,
  hideValidationError: P.bool,
};

export default React.memo(MultipleSelectField);
